import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import type { FetcherWithComponents } from "react-router";
import { AppLink, ArrowLink, Button, Checkbox, FormStatus, Input } from "~/components/ui";

/** The public anchor of the panel — the footer, the auto-test result and Método point here. */
export const WAITLIST_ANCHOR = "ritual-core-waitlist";

type WaitlistStatus = "idle" | "sending" | "error";

export type WaitlistErrors = {
  email?: boolean;
  consent?: boolean;
  generic?: boolean;
};

type WaitlistPanelProps = {
  Form: FetcherWithComponents<unknown>["Form"];
  status: WaitlistStatus;
  errors: WaitlistErrors;
  succeeded: boolean;
  email: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onFieldChange: (field: "email" | "consent") => void;
};

/**
 * T872 S3 — the Ritual Core waitlist (October delivery `components/WaitlistForm.html`) on
 * the S2 primitives: `Input`, `Checkbox`, `FormStatus` and the secondary public button —
 * the self-test stays the page's primary action. States: idle · sending (controls locked,
 * `aria-busy`) · field errors (each under its field, focus on the first) · a failed send
 * (the one `role="alert"` line, the draft untouched so she retries without retyping) ·
 * success (`WaitlistSuccess` replaces the form, focus on its heading). Arriving by the
 * anchor moves the focus to the panel's heading. Submission state is owned by the route
 * view; this component is presentational. No counter and no mailto fallback (the founder,
 * 2026-10-08): a server response is the only success (R-T872-5).
 */
export function WaitlistPanel({ Form, status, errors, succeeded, email, onSubmit, onFieldChange }: WaitlistPanelProps) {
  const { t } = useTranslation("home");
  const titleRef = useRef<HTMLHeadingElement>(null);
  const sending = status === "sending";

  useEffect(() => {
    const focusOnAnchor = () => {
      if (window.location.hash === `#${WAITLIST_ANCHOR}`) titleRef.current?.focus({ preventScroll: true });
    };
    focusOnAnchor();
    window.addEventListener("hashchange", focusOnAnchor);
    return () => window.removeEventListener("hashchange", focusOnAnchor);
  }, []);

  return (
    <section className="pub-waitlist" id={WAITLIST_ANCHOR} aria-labelledby="pub-waitlist-title">
      <p className="pub-eyebrow">{t("waitlist.eyebrow")}</p>
      <h2 id="pub-waitlist-title" tabIndex={-1} ref={titleRef}>
        {t("waitlist.title_lead")}
        <br />
        <em>{t("waitlist.title_accent")}</em>
      </h2>
      <p className="pub-waitlist-body">{t("waitlist.body")}</p>

      {succeeded ? (
        <WaitlistSuccess email={email} />
      ) : (
        <Form method="post" className="pub-waitlist-form" onSubmit={onSubmit} noValidate>
          <Input
            variant="public"
            id="waitlist-email"
            name="email"
            type="email"
            label={t("waitlist.email_label")}
            placeholder={t("waitlist.email_placeholder")}
            autoComplete="email"
            required
            maxLength={254}
            disabled={sending}
            error={errors.email ? t("waitlist.errors.email") : undefined}
            onChange={() => onFieldChange("email")}
          />
          <Input
            variant="public"
            id="waitlist-name"
            name="name"
            type="text"
            label={t("waitlist.name_label")}
            optional={t("waitlist.optional")}
            autoComplete="given-name"
            maxLength={160}
            disabled={sending}
          />
          <Checkbox
            variant="public"
            id="waitlist-consent"
            name="consent"
            disabled={sending}
            onChange={() => onFieldChange("consent")}
            error={errors.consent ? t("waitlist.errors.consent") : undefined}
            label={
              <>
                {t("waitlist.consent_pre")}
                <AppLink to="/legal/privacy" target="_blank" rel="noopener noreferrer">
                  {t("waitlist.consent_link")}
                </AppLink>
                {t("waitlist.consent_post")}
              </>
            }
          />
          <FormStatus variant="public" tone="alert" id="waitlist-status">
            {errors.generic ? t("waitlist.errors.generic") : null}
          </FormStatus>
          <Button type="submit" variant="public-secondary" className="is-full" isLoading={sending} busyLabel={t("waitlist.sending")}>
            {t("waitlist.cta")}
          </Button>
          <p className="pub-meta">{t("waitlist.meta")}</p>
        </Form>
      )}
    </section>
  );
}

/** The confirmed state: heading with focus, the email she typed, and the next step. */
export function WaitlistSuccess({ email }: { email: string }) {
  const { t } = useTranslation("home");
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  return (
    <div className="pub-waitlist-success">
      <h3 tabIndex={-1} ref={headingRef}>
        {t("waitlist.success.title")}
      </h3>
      <p>
        <Trans t={t} i18nKey="waitlist.success.body" values={{ email }} components={{ strong: <strong /> }} />
      </p>
      <ArrowLink routeKey="metodo" arrow="→">
        {t("waitlist.success.link")}
      </ArrowLink>
    </div>
  );
}
